import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  MenuItem, OutlinedInput, Select, Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { stringify } from 'qs';
import BigButtonNext from 'pages/reservationBookingOnlineV2/components/BigButtonNext';
import DesiredStoreOptions from 'pages/reservationBookingOnlineV2/components/DesiredStoreOptions';
import SectionHeader from 'pages/reservationBookingOnlineV2/components/SectionHeader';
import {
  getCompanyLogosApi,
  getDesiredCourseApi,
  getDesiredAreaApi,
  getDesiredAreaExistingClientApi,
  getDesiredStoreApi,
  getDesiredStoreExistingClientApi,
} from 'api/onlineBookingV2Api';
import {
  TDesiredCourse,
  TDesiredArea,
  TDesiredStore,
} from 'pages/reservationBookingOnlineV2/pages/StepsBeforeReservation/types';
import { getReservationChangeInfo } from 'api/reservationChangeApi';
import { getNewAndExistReservationClientCourse } from 'api/onlineBookingApi';
import { INewAndExistReservationClientCourse } from 'pages/reservationBookingOnline/type';

// DB の class_type ('1'=FEMALE, '2'=MALE) を BE API の enum 名に変換
const classTypeCodeToEnumName = (raw: string): string => {
  if (raw === '1') return 'FEMALE';
  if (raw === '2') return 'MALE';
  return '';
};

export interface MenuReselectState {
  customerClass: string;
  classType: string;
  chosenServiceId: string;
  chosenServiceName: string;
  chosenCareId: string;
  chosenCareName: string;
  chosenCourseId: string;
  chosenCourseName: string;
  chosenCourseAcceptRequestReservation: boolean;
  chosenAreaId: string;
  chosenAreaName: string;
  chosenStoreId: string;
  chosenStoreName: string;
  areaStoreList: { id: string; text: string }[];
  reservationId: string; // R-4: 編集中予約 ID (cross-pool / booked 検査から自身を除外)
}

const useStyles = makeStyles((theme: Theme) => createStyles({
  pageRoot: {
    width: '100%',
    minHeight: '100vh',
    backgroundColor: '#fff',
    fontFamily: 'Noto Sans JP',
  },
  contentWrapper: {
    maxWidth: 980,
    width: '95%',
    marginTop: 30,
    marginBottom: 45,
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginTop: 8,
      marginBottom: 8,
    },
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    padding: '24px 16px 0',
  },
  logoImage: {
    maxHeight: '80px',
    maxWidth: '260px',
    objectFit: 'contain' as const,
    [theme.breakpoints.down('sm')]: {
      maxHeight: '48px',
      maxWidth: '160px',
    },
  },
  stepsHeader: {
    textAlign: 'center',
    padding: '32px 16px 8px',
  },
  stepsHeaderMain: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#1a1a1a',
    letterSpacing: '0.5px',
    [theme.breakpoints.down('sm')]: { fontSize: '18px' },
  },
  stepsFlow: {
    display: 'flex',
    alignItems: 'center',
    margin: '24px 0 48px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      margin: '16px 0 32px',
      gap: '0px',
    },
  },
  stepActive: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '22px 16px',
    color: '#fff',
    fontWeight: 700,
    fontSize: '15px',
    letterSpacing: '0.8px',
    textAlign: 'center',
    background: 'linear-gradient(135deg, #C0392B 0%, #E74C3C 100%)',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(192, 57, 43, 0.35)',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: '14px 16px',
      borderRadius: '6px',
    },
  },
  stepInactive: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '22px 16px',
    color: '#fff',
    fontWeight: 700,
    fontSize: '15px',
    letterSpacing: '0.8px',
    textAlign: 'center',
    background: 'linear-gradient(135deg, #E74C3C 0%, #F1736A 100%)',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(231, 76, 60, 0.25)',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: '14px 16px',
      borderRadius: '6px',
    },
  },
  stepArrow: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#BBBBBB',
    fontSize: '20px',
    padding: '0 6px',
    userSelect: 'none',
    '&::after': { content: '"▶"' },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: '4px 0',
      fontSize: '14px',
      '&::after': { content: '"▼"' },
    },
  },
  desireServiceSection: {
    marginTop: '26px',
    marginRight: '80px',
    marginLeft: '80px',
    [theme.breakpoints.down('sm')]: {
      marginTop: '22px',
      marginRight: '10px',
      marginLeft: '10px',
    },
  },
  serviceItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      marginBottom: '28px',
    },
  },
  serviceLabel: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    minWidth: '260px',
    backgroundColor: '#EBEBEB',
    paddingLeft: '8px',
    marginRight: '25px',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      width: '100%',
      padding: '8px',
      marginBottom: '18px',
      marginRight: '0px',
    },
    '& p': {
      fontFamily: 'Noto Sans JP',
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '26px',
      letterSpacing: '0.8px',
      color: '#1a1a1a',
      [theme.breakpoints.down('sm')]: {
        fontSize: '15px',
        lineHeight: '23px',
        marginTop: 0,
        marginBottom: 0,
      },
    },
  },
  serviceLabelStretch: {
    alignSelf: 'stretch',
    [theme.breakpoints.down('sm')]: { alignSelf: 'auto' },
  },
  selectControl: {
    width: '100%',
    fontFamily: 'Noto Sans JP',
    fontSize: '18px',
    fontWeight: 500,
    letterSpacing: '0.8px',
    color: '#606060',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#b9bebe' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#CD0021' },
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  backLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textDecoration: 'underline',
    textAlign: 'center' as const,
    display: 'block',
    marginTop: theme.spacing(1.5),
    fontSize: '1rem',
  },
}));

function MenuReselectPage() {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token') || '';
  const companyCode = urlParams.get('companyCode') || '';

  const [customerClass, setCustomerClass] = useState('');
  // 元予約から引き継ぐ情報（メニュー再選択モードのフィルタ条件）
  const [originalClassType, setOriginalClassType] = useState('');
  const [originalContractCourseCategoryId, setOriginalContractCourseCategoryId] = useState('');
  const [reservationId, setReservationId] = useState('');

  // コース一覧（customerClass による出元の違いを吸収）
  const [newClientCourseList, setNewClientCourseList] = useState<TDesiredCourse[]>([]);
  const [existingCourseList, setExistingCourseList] = useState<INewAndExistReservationClientCourse[]>([]);

  // 共通
  const [areaList, setAreaList] = useState<TDesiredArea[]>([]);
  const [storeList, setStoreList] = useState<TDesiredStore[]>([]);

  const [chosenCourseId, setChosenCourseId] = useState('');
  const [chosenCourseName, setChosenCourseName] = useState('');
  const [chosenCourseAcceptRequestReservation, setChosenCourseAcceptRequestReservation] = useState(false);
  const [chosenAreaId, setChosenAreaId] = useState('');
  const [chosenAreaName, setChosenAreaName] = useState('');
  const [chosenStoreId, setChosenStoreId] = useState('');
  const [chosenStoreName, setChosenStoreName] = useState('');
  const [logoUrls, setLogoUrls] = useState<string[]>([]);

  useEffect(() => {
    if (!companyCode) return;
    const fetchLogos = async () => {
      try {
        const urls = await getCompanyLogosApi(companyCode);
        setLogoUrls(urls || []);
      } catch (error) {
        // ロゴ取得失敗は無視（表示しないだけ）
      }
    };
    fetchLogos();
  }, [companyCode]);

  useEffect(() => {
    if (!token || !companyCode) return;
    getReservationChangeInfo(token, companyCode)
      .then((res: any) => {
        setCustomerClass(res.data?.customerClass || '');
        setOriginalClassType(res.data?.classType || '');
        setOriginalContractCourseCategoryId(res.data?.contractCourseCategoryId || '');
        setReservationId(res.data?.reservationInformationId || '');
      })
      .catch(() => {});
  }, [token, companyCode]);

  // 新規客: BE 側で classType × contractCourseCategoryId 絞り込みされたコース一覧を取得
  useEffect(() => {
    if (customerClass !== '1' || !companyCode || !originalClassType || !originalContractCourseCategoryId) return;
    const apiClassType = classTypeCodeToEnumName(originalClassType);
    if (!apiClassType) return;
    getDesiredCourseApi({
      companyCode,
      classType: apiClassType,
      contractCourseCategoryId: originalContractCourseCategoryId,
    })
      .then((res) => setNewClientCourseList(res))
      .catch(() => enqueueSnackbar('コース一覧の取得に失敗しました。', { variant: 'error' }));
  }, [customerClass, companyCode, originalClassType, originalContractCourseCategoryId, enqueueSnackbar]);

  // 既存客: 全件取得して FE で絞り込み
  useEffect(() => {
    if (customerClass !== '2' || !companyCode) return;
    getNewAndExistReservationClientCourse({ coustomerClass: 'OLD_CUSTOMER', companyCode })
      .then((res) => setExistingCourseList(res))
      .catch(() => enqueueSnackbar('コース一覧の取得に失敗しました。', { variant: 'error' }));
  }, [customerClass, companyCode, enqueueSnackbar]);

  // コース一覧を customerClass を吸収した共通フォーマットに正規化
  type CourseOption = {
    id: string;
    name: string;
    acceptRequestReservation: boolean;
  };
  const courseOptions: CourseOption[] = useMemo(() => {
    if (customerClass === '1') {
      return newClientCourseList.map((c) => ({
        id: c.contractCourseMstId,
        name: c.courseName,
        acceptRequestReservation: !!c.acceptRequestReservation,
      }));
    }
    if (customerClass === '2') {
      const filtered = (originalClassType && originalContractCourseCategoryId)
        ? existingCourseList.filter(
          (c) => c.classType === originalClassType
            && c.contractCourseCategoryId === originalContractCourseCategoryId,
        )
        : existingCourseList;
      return filtered.map((c) => ({
        id: c.courseId,
        name: c.courseName,
        acceptRequestReservation: !!c.acceptRequestReservation,
      }));
    }
    return [];
  }, [customerClass, newClientCourseList, existingCourseList, originalClassType, originalContractCourseCategoryId]);

  const loadAreaList = useCallback((courseId: string) => {
    if (!courseId) { setAreaList([]); return; }
    const fetcher = customerClass === '2' ? getDesiredAreaExistingClientApi : getDesiredAreaApi;
    fetcher({ companyCode, contractCourseMstId: courseId })
      .then((res) => setAreaList(res))
      .catch(() => enqueueSnackbar('エリア一覧の取得に失敗しました。', { variant: 'error' }));
  }, [companyCode, customerClass, enqueueSnackbar]);

  const loadStoreList = useCallback((areaId: string, courseId: string) => {
    if (!areaId || !courseId) { setStoreList([]); return; }
    const fetcher = customerClass === '2' ? getDesiredStoreExistingClientApi : getDesiredStoreApi;
    fetcher({ companyCode, contractCourseMstId: courseId, areaId })
      .then((res) => setStoreList(res))
      .catch(() => enqueueSnackbar('店舗一覧の取得に失敗しました。', { variant: 'error' }));
  }, [companyCode, customerClass, enqueueSnackbar]);

  const handleCourseChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const id = e.target.value as string;
    const item = courseOptions.find((c) => c.id === id);
    setChosenCourseId(id);
    setChosenCourseName(item?.name || '');
    setChosenCourseAcceptRequestReservation(!!item?.acceptRequestReservation);
    setChosenAreaId(''); setChosenAreaName('');
    setChosenStoreId(''); setChosenStoreName('');
    setAreaList([]); setStoreList([]);
    if (id) loadAreaList(id);
  };

  const handleAreaChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const id = e.target.value as string;
    const item = areaList.find((a) => a.areaId === id);
    setChosenAreaId(id);
    setChosenAreaName(item?.areaName || '');
    setChosenStoreId(''); setChosenStoreName('');
    setStoreList([]);
    loadStoreList(id, chosenCourseId);
  };

  const handleStoreClick = (id: string, name: string) => {
    setChosenStoreId(id);
    setChosenStoreName(name);
  };

  const handleSubmit = () => {
    if (!chosenCourseId || !chosenAreaId || !chosenStoreId) {
      enqueueSnackbar('すべての項目を選択してください', { variant: 'warning' });
      return;
    }
    const state: MenuReselectState = {
      customerClass,
      classType: originalClassType,
      // 元予約のサービス・カテゴリで固定（後続画面の互換のため）
      chosenServiceId: '',
      chosenServiceName: '',
      chosenCareId: originalContractCourseCategoryId,
      chosenCareName: '',
      chosenCourseId,
      chosenCourseName,
      chosenCourseAcceptRequestReservation,
      chosenAreaId,
      chosenAreaName,
      chosenStoreId,
      chosenStoreName,
      areaStoreList: storeList.map((s) => ({ id: s.orgId, text: s.orgName })),
      reservationId,
    };
    history.push(
      `/contact/reservationMenuReselectDate?${stringify({ token, companyCode })}`,
      state,
    );
  };

  const handleBack = () => {
    history.replace(`/contact/reservationChange?${stringify({ token, companyCode })}`);
  };

  return (
    <div className={classes.pageRoot}>
      <div className={classes.contentWrapper}>
        {logoUrls.length > 0 && (
          <div className={classes.logoContainer}>
            {logoUrls.map((url) => (
              <img key={url} src={url} alt="" className={classes.logoImage} />
            ))}
          </div>
        )}
        <div className={classes.stepsHeader}>
          <Typography className={classes.stepsHeaderMain}>{'たった1分\u3000簡単3ステップ予約！'}</Typography>
        </div>
        <div className={classes.stepsFlow}>
          <div className={classes.stepActive}>①コース・店舗を選ぶ</div>
          <div className={classes.stepArrow} aria-hidden="true" />
          <div className={classes.stepInactive}>②日時を選ぶ</div>
        </div>

        <SectionHeader title="1. ご希望のサービス・サロンの選択" />

        <div className={classes.desireServiceSection}>
          <div className={classes.serviceItem}>
            <div className={classes.serviceLabel}>
              <p>ご希望のコース：</p>
            </div>
            <Select
              fullWidth={true}
              className={classes.selectControl}
              value={chosenCourseId}
              onChange={handleCourseChange}
              displayEmpty={true}
              input={<OutlinedInput />}
            >
              <MenuItem value="">---------</MenuItem>
              {courseOptions.map((item) => (
                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
              ))}
            </Select>
          </div>

          <div className={classes.serviceItem}>
            <div className={classes.serviceLabel}>
              <p>ご希望のエリア：</p>
            </div>
            <Select
              fullWidth={true}
              className={classes.selectControl}
              value={chosenAreaId}
              onChange={handleAreaChange}
              displayEmpty={true}
              disabled={!chosenCourseId}
              input={<OutlinedInput />}
            >
              <MenuItem value="">---------</MenuItem>
              {areaList.map((item) => (
                <MenuItem key={item.areaId} value={item.areaId}>{item.areaName}</MenuItem>
              ))}
            </Select>
          </div>

          <div className={classes.serviceItem}>
            <div className={clsx(classes.serviceLabel, classes.serviceLabelStretch)}>
              <p>ご希望の店舗：</p>
            </div>
            <DesiredStoreOptions
              title="ご希望のサロンをお選びください"
              list={storeList.map((s) => ({ id: s.orgId, text: s.orgName }))}
              defaultOption={chosenStoreId}
              disabled={!chosenAreaId}
              handleClick={handleStoreClick}
            />
          </div>
        </div>

        <div className={classes.buttonContainer}>
          <BigButtonNext content="ご来店日時選択へ" handleSubmit={handleSubmit} isDisabled={!chosenStoreId} />
        </div>
        <Typography
          component="span"
          className={classes.backLink}
          onClick={handleBack}
        >
          戻る
        </Typography>
      </div>
    </div>
  );
}

export default MenuReselectPage;
